// src/pages/BulkImport.jsx
import { useState } from 'react';
import { supabase } from '../services/supabaseClient';

const ADMIN_PASSWORD = 'green123';

// ── CSV Template ───────────────────────────────────────────────
const CSV_TEMPLATE = `name,price,category,tag,description,image_url,in_stock
Snake Plant,349,Indoor,Low Maintenance,Hardy plant perfect for beginners. Purifies air.,https://images.unsplash.com/photo-1599598425947-5202edd56fde?w=400&q=80,true
Money Plant,149,Indoor,Air Purifier,Brings good luck easy to grow in water or soil.,https://images.unsplash.com/photo-1631377819268-d716cd610cd2?w=400&q=80,true`;

export default function BulkImport() {
  const [authed,    setAuthed]    = useState(false);
  const [password,  setPassword]  = useState('');
  const [authErr,   setAuthErr]   = useState('');

  const [csvText,   setCsvText]   = useState('');
  const [parsed,    setParsed]    = useState([]);
  const [parseErr,  setParseErr]  = useState('');
  const [importing, setImporting] = useState(false);
  const [progress,  setProgress]  = useState(0);
  const [results,   setResults]   = useState(null);
  const [step,      setStep]      = useState(1); // 1=paste, 2=preview, 3=done

  // ── Parse CSV ────────────────────────────────────────────────
  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return { error: 'CSV must have at least a header row and one data row.' };

    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const required = ['name', 'price', 'category'];
    const missing  = required.filter(r => !headers.includes(r));
    if (missing.length > 0) return { error: `Missing required columns: ${missing.join(', ')}` };

    const rows = [];
    const errors = [];

    for (let i = 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Handle commas inside quoted fields
      const cols = [];
      let current = '';
      let inQuotes = false;
      for (let ch of line) {
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { cols.push(current.trim()); current = ''; }
        else { current += ch; }
      }
      cols.push(current.trim());

      const row = {};
      headers.forEach((h, idx) => { row[h] = cols[idx] || ''; });

      if (!row.name) { errors.push(`Row ${i}: Name is required`); continue; }
      const price = Number(row.price);
      if (!price || price <= 0) { errors.push(`Row ${i}: Valid price required for "${row.name}"`); continue; }

      rows.push({
        name:        row.name,
        price:       price,
        category:    row.category || 'Indoor',
        tag:         row.tag || '',
        description: row.description || '',
        image_url:   row.image_url || '',
        in_stock:    row.in_stock?.toLowerCase() !== 'false',
      });
    }

    return { rows, errors };
  };

  const handleParse = () => {
    setParseErr('');
    setParsed([]);
    if (!csvText.trim()) { setParseErr('Please paste CSV data first.'); return; }
    const { rows, errors, error } = parseCSV(csvText);
    if (error) { setParseErr(error); return; }
    if (rows.length === 0) { setParseErr('No valid rows found. Check your CSV format.'); return; }
    setParsed(rows);
    if (errors.length > 0) setParseErr(`⚠️ ${errors.length} rows skipped: ${errors.join(' | ')}`);
    setStep(2);
  };

  // ── Import to Supabase ────────────────────────────────────────
  const handleImport = async () => {
    setImporting(true);
    setProgress(0);
    let success = 0;
    let failed  = 0;
    const failedItems = [];

    // Import in batches of 10
    const batchSize = 10;
    for (let i = 0; i < parsed.length; i += batchSize) {
      const batch = parsed.slice(i, i + batchSize);
      try {
        const { error } = await supabase.from('products').insert(batch);
        if (error) {
          failed  += batch.length;
          failedItems.push(...batch.map(b => b.name));
        } else {
          success += batch.length;
        }
      } catch {
        failed  += batch.length;
        failedItems.push(...batch.map(b => b.name));
      }
      setProgress(Math.round(((i + batchSize) / parsed.length) * 100));
    }

    setResults({ success, failed, failedItems });
    setImporting(false);
    setStep(3);
  };

  const reset = () => {
    setCsvText('');
    setParsed([]);
    setParseErr('');
    setProgress(0);
    setResults(null);
    setStep(1);
  };

  const downloadTemplate = () => {
    const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'plants_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Login ────────────────────────────────────────────────────
  if (!authed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 pt-16">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-8 w-full max-w-sm">
          <div className="text-center mb-6">
            <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <span className="text-2xl">📦</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900">Bulk Import</h2>
            <p className="text-sm text-gray-400 mt-1">Admin access required</p>
          </div>
          {authErr && <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm">{authErr}</div>}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Password</label>
              <input type="password" value={password}
                onChange={e => { setPassword(e.target.value); setAuthErr(''); }}
                onKeyDown={e => e.key === 'Enter' && (password === ADMIN_PASSWORD ? setAuthed(true) : setAuthErr('Incorrect password.'))}
                placeholder="Enter admin password"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32]" />
            </div>
            <button onClick={() => password === ADMIN_PASSWORD ? setAuthed(true) : setAuthErr('Incorrect password.')}
              className="w-full py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
              Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pt-16">
      <div className="bg-green-50 border-b border-green-100 text-center py-2 text-sm text-gray-600">
        Serving <span className="font-semibold text-gray-800">Ulwe, Navi Mumbai - 410206</span>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">📦 Bulk Import Plants</h1>
            <p className="text-sm text-gray-500 mt-0.5">Import 100 plants at once from Excel/CSV</p>
          </div>
          <a href="/admin" className="px-4 py-2 border border-gray-200 bg-white text-sm font-medium text-gray-600 rounded-xl hover:bg-gray-50 transition-colors">
            ← Back to Admin
          </a>
        </div>

        {/* Step indicator */}
        <div className="flex items-center gap-0 mb-8">
          {['Paste CSV', 'Preview', 'Done'].map((label, i) => {
            const num = i + 1; const active = step === num; const done = step > num;
            return (
              <div key={label} className="flex items-center">
                <div className="flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold border-2 transition-all
                    ${done ? 'bg-[#2e7d32] border-[#2e7d32] text-white' : active ? 'border-[#2e7d32] text-[#2e7d32]' : 'border-gray-300 text-gray-400'}`}>
                    {done ? '✓' : num}
                  </div>
                  <span className={`text-xs mt-1 font-medium ${active || done ? 'text-[#2e7d32]' : 'text-gray-400'}`}>{label}</span>
                </div>
                {i < 2 && <div className={`w-20 sm:w-32 h-0.5 mb-4 mx-1 ${step > num ? 'bg-[#2e7d32]' : 'bg-gray-200'}`} />}
              </div>
            );
          })}
        </div>

        {/* ── STEP 1: Paste CSV ── */}
        {step === 1 && (
          <div className="space-y-5">
            {/* Instructions */}
            <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5">
              <h3 className="font-bold text-blue-900 mb-3">📋 How to Import</h3>
              <ol className="space-y-2 text-sm text-blue-800">
                <li>1. Download the Excel file provided (BalconyGreen_Plants_100.xlsx)</li>
                <li>2. Open it in Excel → <strong>File → Save As → CSV (Comma delimited)</strong></li>
                <li>3. Open the saved CSV file in Notepad</li>
                <li>4. Select all (Ctrl+A) → Copy (Ctrl+C)</li>
                <li>5. Paste below and click <strong>Parse & Preview</strong></li>
              </ol>
              <button onClick={downloadTemplate}
                className="mt-3 px-4 py-2 bg-blue-600 text-white text-xs font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                📥 Download Sample CSV Template
              </button>
            </div>

            {/* CSV Format reference */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <h3 className="font-bold text-gray-900 mb-3">Required CSV Format</h3>
              <div className="bg-gray-50 rounded-xl p-3 font-mono text-xs text-gray-600 overflow-x-auto">
                name,price,category,tag,description,image_url,in_stock<br/>
                Snake Plant,349,Indoor,Low Maintenance,Great beginner plant,https://...,true<br/>
                Aloe Vera,199,Outdoor,Medicinal,Soothing gel for skin,https://...,true
              </div>
              <div className="mt-3 grid sm:grid-cols-3 gap-2 text-xs">
                {[
                  ['name *', 'Plant name'],
                  ['price *', 'Number only (349)'],
                  ['category *', 'Indoor / Outdoor / Flowering'],
                  ['tag', 'e.g. Air Purifier'],
                  ['description', 'Short description'],
                  ['image_url', 'Unsplash or other URL'],
                  ['in_stock', 'true or false'],
                ].map(([col, desc]) => (
                  <div key={col} className="bg-green-50 rounded-lg p-2">
                    <span className="font-bold text-[#2e7d32]">{col}</span>
                    <span className="text-gray-500 ml-1">— {desc}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Paste area */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
              <label className="block font-bold text-gray-900 mb-3">Paste CSV Data Here</label>
              <textarea
                value={csvText}
                onChange={e => { setCsvText(e.target.value); setParseErr(''); }}
                placeholder="name,price,category,tag,description,image_url,in_stock&#10;Snake Plant,349,Indoor,Low Maintenance,Great plant for beginners,https://images.unsplash.com/...,true&#10;Money Plant,149,Indoor,Air Purifier,Easy to grow,https://images.unsplash.com/...,true"
                rows={12}
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#2e7d32]/30 focus:border-[#2e7d32] resize-none"
              />
              {parseErr && (
                <div className="mt-2 px-4 py-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl text-sm">
                  {parseErr}
                </div>
              )}
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-gray-400">
                  {csvText.trim().split('\n').filter(l => l.trim()).length - 1} rows detected
                </span>
                <button onClick={handleParse}
                  className="px-6 py-2.5 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
                  Parse & Preview →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── STEP 2: Preview ── */}
        {step === 2 && (
          <div className="space-y-5">
            {/* Summary */}
            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 flex items-center justify-between">
              <div>
                <p className="font-bold text-[#2e7d32] text-lg">{parsed.length} plants ready to import</p>
                <p className="text-sm text-gray-500 mt-0.5">Review below before importing</p>
              </div>
              <div className="flex gap-3">
                <button onClick={reset}
                  className="px-4 py-2 border border-gray-200 text-gray-600 text-sm font-medium rounded-xl hover:bg-gray-50 transition-colors">
                  ← Back
                </button>
                <button onClick={handleImport} disabled={importing}
                  className="px-6 py-2.5 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors disabled:opacity-60">
                  {importing ? `Importing... ${Math.min(progress, 100)}%` : `🚀 Import ${parsed.length} Plants`}
                </button>
              </div>
            </div>

            {/* Progress bar */}
            {importing && (
              <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                <div className="flex justify-between mb-2">
                  <span className="text-sm font-medium text-gray-700">Importing plants...</span>
                  <span className="text-sm font-bold text-[#2e7d32]">{Math.min(progress, 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div
                    className="bg-[#2e7d32] h-3 rounded-full transition-all duration-300"
                    style={{ width: `${Math.min(progress, 100)}%` }}
                  />
                </div>
              </div>
            )}

            {parseErr && (
              <div className="px-4 py-3 bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-xl text-sm">
                {parseErr}
              </div>
            )}

            {/* Preview table */}
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50">
                    {['#', 'Name', 'Price', 'Category', 'Tag', 'Image', 'Stock'].map(h => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {parsed.map((p, i) => (
                    <tr key={i} className="hover:bg-gray-50/60">
                      <td className="px-4 py-2.5 text-gray-400 text-xs">{i + 1}</td>
                      <td className="px-4 py-2.5 font-medium text-gray-900">{p.name}</td>
                      <td className="px-4 py-2.5 font-bold text-[#2e7d32]">₹{p.price}</td>
                      <td className="px-4 py-2.5 text-gray-600">{p.category}</td>
                      <td className="px-4 py-2.5 text-gray-500 text-xs">{p.tag || '—'}</td>
                      <td className="px-4 py-2.5">
                        {p.image_url ? (
                          <img src={p.image_url} alt={p.name}
                            className="w-10 h-10 rounded-lg object-cover"
                            onError={e => { e.target.style.display = 'none'; }}
                          />
                        ) : <span className="text-gray-300 text-lg">🌿</span>}
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${p.in_stock ? 'bg-green-100 text-[#2e7d32]' : 'bg-red-100 text-red-600'}`}>
                          {p.in_stock ? 'In Stock' : 'Hidden'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ── STEP 3: Done ── */}
        {step === 3 && results && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center">
            <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${results.failed === 0 ? 'bg-green-100' : 'bg-yellow-100'}`}>
              <span className="text-3xl">{results.failed === 0 ? '🎉' : '⚠️'}</span>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">
              {results.failed === 0 ? 'Import Complete!' : 'Import Partially Done'}
            </h2>

            <div className="flex justify-center gap-6 my-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-[#2e7d32]">{results.success}</div>
                <div className="text-sm text-gray-500">Successfully Added</div>
              </div>
              {results.failed > 0 && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-500">{results.failed}</div>
                  <div className="text-sm text-gray-500">Failed</div>
                </div>
              )}
            </div>

            {results.failed > 0 && (
              <div className="bg-red-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-sm font-semibold text-red-700 mb-1">Failed plants:</p>
                <p className="text-xs text-red-600">{results.failedItems.join(', ')}</p>
              </div>
            )}

            <p className="text-gray-500 text-sm mb-8">
              All imported plants are now visible in your Shop page!
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <a href="/admin"
                className="px-6 py-3 bg-[#2e7d32] text-white font-semibold rounded-xl hover:bg-[#1b5e20] transition-colors">
                Go to Admin Dashboard
              </a>
              <a href="/shop"
                className="px-6 py-3 border-2 border-[#2e7d32] text-[#2e7d32] font-semibold rounded-xl hover:bg-green-50 transition-colors">
                View Shop
              </a>
              <button onClick={reset}
                className="px-6 py-3 border border-gray-200 text-gray-600 font-medium rounded-xl hover:bg-gray-50 transition-colors">
                Import More
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
